import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "@emotion/styled";

import {
  Avatar as MuiAvatar,
  Badge,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover as MuiPopover,
  SvgIcon,
  Tooltip,
  Typography,
} from "@mui/material";
import { Bell } from "react-feather";

import { fetchApi } from "@/utils/fetchApi";
import { markItemsRead, markAllItemsRead } from "@/redux/slices/notifications";
import { getNotificationType, resolveNotificationRoute } from "@/constants/notificationTypes";

const Popover = styled(MuiPopover)`
  .MuiPaper-root {
    width: 300px;
    ${(props) => props.theme.shadows[1]};
    border: 1px solid ${(props) => props.theme.palette.divider};
  }
`;

const Indicator = styled(Badge)`
  .MuiBadge-badge {
    background: ${(props) => props.theme.header.indicator.background};
    color: ${(props) => props.theme.palette.common.white};
  }
`;

const Avatar = styled(MuiAvatar)`
  background: ${(props) => props.theme.palette.primary.main};
`;

const NotificationHeader = styled(Box)`
  text-align: center;
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
`;

function Notification({ title, description, Icon, unread, onClick }) {
  return (
    <ListItem divider button onClick={onClick} sx={unread ? { backgroundColor: "action.hover" } : undefined}>
      <ListItemAvatar>
        <Avatar>
          <SvgIcon fontSize="small">
            <Icon />
          </SvgIcon>
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={title}
        primaryTypographyProps={{
          variant: "subtitle2",
          color: "textPrimary",
        }}
        secondary={description}
        secondaryTypographyProps={{ noWrap: true }}
      />
    </ListItem>
  );
}

// Max rows to show in the dropdown preview; the full list lives at /or/notifications.
const PREVIEW_LIMIT = 7;

function NavbarNotificationsDropdown() {
  const ref = useRef(null);
  const [isOpen, setOpen] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const items = useSelector((state) => state.notifications.items);
  const unreadCount = useSelector((state) => state.notifications.unreadCount);

  const previewItems = (items || []).slice(0, PREVIEW_LIMIT);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleItemClick = (notification) => {
    setOpen(false);
    // Optimistic mark-read, then fire-and-forget the API call.
    if (!notification.IS_READ) {
      dispatch(markItemsRead([notification.NOTIFICATIONID]));
      fetchApi("notifications", "markRead", { ids: [notification.NOTIFICATIONID] }).catch((err) =>
        console.error("Failed to mark notification read", err),
      );
    }
    navigate(resolveNotificationRoute(notification));
  };

  const handleMarkAllRead = () => {
    dispatch(markAllItemsRead());
    fetchApi("notifications", "markAllRead", {}).catch((err) =>
      console.error("Failed to mark all notifications read", err),
    );
  };

  const handleShowAll = () => {
    setOpen(false);
    navigate("/or/notifications");
  };

  return (
    <React.Fragment>
      <Tooltip title="Notifications">
        <IconButton color="inherit" ref={ref} onClick={handleOpen} size="large">
          <Indicator badgeContent={unreadCount} max={99}>
            <Bell />
          </Indicator>
        </IconButton>
      </Tooltip>
      <Popover
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
      >
        <NotificationHeader p={2}>
          <Typography variant="subtitle1" color="textPrimary">
            {unreadCount > 0 ? `${unreadCount} New Notification${unreadCount === 1 ? "" : "s"}` : "Notifications"}
          </Typography>
        </NotificationHeader>
        <React.Fragment>
          {previewItems.length === 0 ? (
            <Box p={3} display="flex" justifyContent="center">
              <Typography variant="body2" color="textSecondary">
                No notifications
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {previewItems.map((notification) => {
                const meta = getNotificationType(notification.EVENT_TYPE);
                return (
                  <Notification
                    key={notification.NOTIFICATIONID}
                    title={notification.TITLE || meta.title}
                    description={notification.BODY || meta.body}
                    Icon={meta.icon}
                    unread={!notification.IS_READ}
                    onClick={() => handleItemClick(notification)}
                  />
                );
              })}
            </List>
          )}
          <Box p={1} display="flex" justifyContent="space-between">
            <Button size="small" onClick={handleMarkAllRead} disabled={unreadCount === 0}>
              Mark all read
            </Button>
            <Button size="small" onClick={handleShowAll}>
              Show all
            </Button>
          </Box>
        </React.Fragment>
      </Popover>
    </React.Fragment>
  );
}

export default NavbarNotificationsDropdown;
